<script setup lang="ts">
import type { CmsElementShopStudioRecipesRecipeListing } from "~/composables/useCustom";
import { useI18n } from "vue-i18n";
import { getSmallestThumbnailUrl } from "@shopware-pwa/helpers-next";
import { ref, computed } from "vue";
const { t } = useI18n();

const props = defineProps<{
  content: CmsElementShopStudioRecipesRecipeListing;
}>();

const data = props.content?.data || {};
const initialListing = data.listing || { elements: [], total: 0 };
const currentPage = ref(1);
const itemsPerPage = ref(24); // Adjust this number as needed
const loadedCount = ref(0);
// Computed property to show limited number of recipes
const displayedRecipes = computed(() => {
  loadedCount.value = currentPage.value * itemsPerPage.value;
  return initialListing.elements.slice(0, loadedCount.value);
});

function loadMore() {
  // Increment the page to show more items
  currentPage.value += 1;
}

function getTime(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  let result = "";
  if (hours > 0) {
    result += t("recipe.time.hour", { count: hours });
  }
  if (remainingMinutes > 0) {
    if (hours > 0) {
      result += " ";
    }
    result += t("recipe.time.minute", { count: remainingMinutes });
  }

  return result;
}

function getThumbnailUrl(media: any) {
  if (media.thumbnails?.length > 0) {
    let thumb = `${getSmallestThumbnailUrl(media)}`;
    if (thumb) {
      return thumb;
    }
  }
  return `${media.url}`;
}

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
</script>

<template>
  <div class="recepies px-4">
    <div class="container mx-auto relative pb-12 sm:pb-16">
      <div
        v-if="displayedRecipes.length > 0"
        class="grid grid-cols-2 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-12"
      >
        <div
          v-for="(recipe, index) in displayedRecipes"
          :key="recipe.id || index"
          class="product product-wrap relative"
        >
          <NuxtLink
            v-if="recipe.seoUrls"
            :to="formatLink('/' + recipe.seoUrls[0]?.seoPathInfo)"
          >
            <div class="prod-img mb-4">
              <figure
                class="flex items-center justify-center truncate rounded-lg"
              >
                <NuxtImg
                  v-if="recipe.cover?.media"
                  class="w-full h-full rounded-lg hover:scale-105 transition duration-300 aspect-square"
                  format="webp"
                  :src="getThumbnailUrl(recipe.cover?.media)"
                  :alt="recipe.title"
                  :width="recipe.cover?.media.metaData.width"
                  :height="recipe.cover?.media.metaData.height"
                  loading="lazy"
                />
                <img
                  v-else
                  src="assets/images/placeholder-product.png"
                  alt="Placeholder image"
                  class="w-full h-full rounded-lg hover:scale-105 transition duration-300"
                />
              </figure>
            </div>
          </NuxtLink>
          <div class="product-detail">
            <NuxtLink
              v-if="recipe.seoUrls"
              :to="formatLink('/' + recipe.seoUrls[0]?.seoPathInfo)"
              class="hover:text-red-bg transition duration-300"
            >
              <h6 class="text-base font-bold mb1.5 line-clamp-1">
                {{ recipe.title }}
              </h6>
            </NuxtLink>
            <div class="post-date recepie flex items-center space-x-3 mt-2">
              <div
                v-if="recipe.totalTime"
                class="time text-xs font-semibold text-davy-grey flex items-center"
              >
                <span class="mr-3"
                  ><img src="assets/icon/delivery-clock.svg" alt="icon"
                /></span>
                {{ getTime(recipe.totalTime) }}
              </div>
              <div
                v-if="recipe?.difficulty"
                class="recepie-h-e text-xs font-semibold text-davy-grey flex items-center"
              >
                <span class="mr-3"
                  ><img src="assets/icon/chart.svg" alt="chart icon"
                /></span>
                {{ $t("recipe.content.difficulty." + recipe?.difficulty) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center text-davy-grey">
        {{ $t("recipe.noRecipes") }}
      </div>

      <template v-if="loadedCount < initialListing.elements.length">
        <div class="show-more text-center mb-5">
          <button
            id="show-more"
            class="btn-default btn-transprent-bg bg-transparent"
            @click="loadMore"
          >
            {{ $t("recipe.loadMore") }}
          </button>
        </div>
        <!-- Progress bar itself -->
        <div class="progress shown w-full md:w-[360px] mx-auto truncate">
          <div class="w-full bg-platinum h-[6px] rounded mb-5 relative">
            <div
              id="progress-bar"
              class="bg-arsenic h-[4px] rounded absolute top-1/2 inset-y-1/2 mt-[-2px]"
              :style="{
                width: `${(loadedCount / initialListing.elements.length) * 100}%`,
              }"
            ></div>
          </div>
          <div
            class="progress-shown flex items-center justify-center text-sm font-semibold text-davy-grey space-x-2"
          >
            <div id="progress-text">
              {{ loadedCount }} {{ $t("product.load.of") }}
              {{ initialListing.elements.length }}
            </div>
            <span>{{ $t("product.load.shown") }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
<style scoped>
@keyframes gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>
